import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoCapture from "@/components/PhotoCapture";
import Mascot from "@/components/Mascot";
import { Button } from "@/components/ui/button";
import { ATT_STATUS } from "@/lib/constants";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Smile, FileText, Thermometer } from "lucide-react";

const STATUS_OPTS = [
  { v: "hadir", label: "Hadir", icon: Smile,
    on: "border-teal-500 bg-teal-50", icon_on: "text-teal-600" },
  { v: "izin", label: "Izin", icon: FileText,
    on: "border-sky-500 bg-sky-50", icon_on: "text-sky-600" },
  { v: "sakit", label: "Sakit", icon: Thermometer,
    on: "border-orange-500 bg-orange-50", icon_on: "text-orange-600" },
];

export default function Attendance() {
  const { user } = useAuth();
  const [today, setToday] = useState(undefined);
  const [status, setStatus] = useState("hadir");
  const [photoPath, setPhotoPath] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const now = new Date();
  const dateStr = now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Jakarta" });

  const load = () => api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => setToday(null));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!photoPath) { toast.error("Ambil foto dulu ya!"); return; }
    setSubmitting(true);
    try {
      await api.post("/attendance", { photo_path: photoPath, status, note });
      toast.success("Absensi terkirim! Menunggu verifikasi guru.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim absensi");
    } finally { setSubmitting(false); }
  };

  if (today === undefined) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Absensi</h1>
        <p className="text-slate-500 mt-1 capitalize">{dateStr} · Kelas {user?.kelas}</p>
      </div>

      {today ? (
        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm text-center" data-testid="attendance-done-card">
          <Mascot size={90} className="mx-auto animate-float" />
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mt-2" />
          <h2 className="font-display text-xl font-bold text-slate-800 mt-2">Kamu sudah absen hari ini!</h2>
          <div className="mt-4 inline-flex items-center gap-2">
            <span className="text-slate-500">Status:</span>
            <span className={`px-4 py-1.5 rounded-full border font-medium ${(ATT_STATUS[today.status] || ATT_STATUS.menunggu).cls}`}>
              {(ATT_STATUS[today.status] || ATT_STATUS.menunggu).label}
            </span>
          </div>
          <p className="text-sm text-slate-400 mt-3">Dikirim jam {today.time} WIB</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-sm space-y-6">
          <div>
            <p className="font-display font-semibold text-slate-800 mb-3">1. Pilih Status Kehadiran</p>
            <div className="grid grid-cols-3 gap-3">
              {STATUS_OPTS.map((o) => (
                <button key={o.v} onClick={() => setStatus(o.v)} data-testid={`att-status-${o.v}`}
                  className={`flex flex-col items-center gap-2 py-4 rounded-2xl border-2 transition-colors ${status === o.v ? o.on : "border-slate-200"}`}>
                  <o.icon className={`w-7 h-7 ${status === o.v ? o.icon_on : "text-slate-400"}`} />
                  <span className={`text-sm font-medium ${status === o.v ? "text-slate-800" : "text-slate-500"}`}>{o.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-display font-semibold text-slate-800 mb-3">2. Ambil Foto Selfie</p>
            <PhotoCapture category="attendance" testIdPrefix="attendance-selfie" label="Buka Kamera" onUploaded={(path) => setPhotoPath(path)} />
          </div>

          <Button onClick={submit} disabled={submitting || !photoPath} className="w-full h-14 rounded-2xl bg-teal-600 hover:bg-teal-700 text-lg font-semibold" data-testid="submit-attendance-btn">
            {submitting && <Loader2 className="w-5 h-5 mr-2 animate-spin" />} Kirim Absensi
          </Button>
        </div>
      )}
    </div>
  );
}
